import { BookText, Check, FilePenLine, FileText, LoaderCircle, Plus, RefreshCw, Sparkles, Trash2, Upload } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { api, patchJson, postJson, putJson } from "../api";
import type { AuthorVoice, ProjectStyleBlend, StyleReference, StyleReferenceDetail } from "../types";
import { Modal } from "./Modal";
import { confirmDiscard, useUnsavedChanges } from "../interaction";
import { AuthorVoicePanel } from "./AuthorVoicePanel";
import { EmptyState, StatusDot, formatNumber } from "./Ui";

type LibraryProps = { projectId: string; notify: (message: string, tone?: "success" | "error" | "info") => void };
export function StyleReferenceLibrary(props: LibraryProps) { return <LibraryEditor key={props.projectId} {...props} />; }
function LibraryEditor({ projectId, notify }: LibraryProps) {
  const alive = useRef(true);
  const loadSequence = useRef(0);
  const selectionLock = useRef(false);
  const [selectionBusy, setSelectionBusy] = useState(false);
  const [authorVoice, setAuthorVoice] = useState<Pick<AuthorVoice, "active" | "version" | "title"> | null>(null);
  const [appliesAfterChapter, setAppliesAfterChapter] = useState<number | null>(null);
  const [references, setReferences] = useState<StyleReference[]>([]);
  const [blend, setBlend] = useState<ProjectStyleBlend>({
    projectId,
    status: "idle",
    sourceReferenceIds: [],
    sourceTitles: [],
    anchorCount: 0,
    anchorCharacters: 0,
    profile: null,
    error: "",
    updatedAt: null
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<StyleReference | null>(null);
  const requestLock = useRef(false);
  const formSnapshot = useRef(["", ""]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const formDirty = modalOpen && (title !== formSnapshot.current[0] || content !== formSnapshot.current[1]);
  useUnsavedChanges(formDirty, "文风参考文本", busy || selectionBusy);
  async function closeEditor() { if (!busy && await confirmDiscard(formDirty, "文风参考文本")) { setModalOpen(false); setTitle(""); setContent(""); } }
  async function retryLoad() { setLoading(true); try { await load(); } catch (error) { if (alive.current) setLoadError(error instanceof Error ? error.message : "文风库加载失败"); } finally { if (alive.current) setLoading(false); } }
  const selected = references.find((item) => item.id === selectedId) ?? null;
  const activeCount = references.filter((item) => item.active).length;

  async function load() {
    const sequence = ++loadSequence.current;
    const result = await api<{ references: StyleReference[]; blend: ProjectStyleBlend; authorVoice: Pick<AuthorVoice, "active" | "version" | "title">; appliesAfterChapter: number | null }>(`/api/projects/${projectId}/style-references`);
    if (!alive.current || sequence !== loadSequence.current) return;
    setLoadError(""); setLoading(false);
    setAuthorVoice(result.authorVoice);
    setAppliesAfterChapter(result.appliesAfterChapter);
    setReferences(result.references);
    setBlend(result.blend);
    setSelectedId((current) => result.references.some((item) => item.id === current) ? current : result.references[0]?.id ?? "");
  }

  useEffect(() => {
    alive.current = true;
    setSelectedId("");
    setBlend({ projectId, status: "idle", sourceReferenceIds: [], sourceTitles: [], anchorCount: 0, anchorCharacters: 0, profile: null, error: "", updatedAt: null });
    void retryLoad();
    const refresh = () => void load().catch(() => undefined);
    window.addEventListener("focus", refresh);
    return () => { alive.current = false; loadSequence.current++; window.removeEventListener("focus", refresh); };
  }, [projectId]);

  useEffect(() => {
    if (!references.some((item) => item.status === "analyzing") && blend.status !== "analyzing") return;
    let polling = false; let cancelled = false;
    const timer = window.setInterval(() => {
      if (polling || cancelled) return; polling = true;
      void load().catch((cause) => { if (alive.current && !cancelled) setLoadError(cause instanceof Error ? cause.message : "文风刷新失败，已保留上次状态"); }).finally(() => { polling = false; });
    }, 3000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [references.some((item) => item.status === "analyzing"), blend.status, projectId]);

  function openCreate() {
    if (requestLock.current || busy) return;
    formSnapshot.current = ["", ""];
    setEditingId("");
    setTitle("");
    setContent("");
    setModalOpen(true);
  }

  async function openEdit(reference: StyleReference) {
    if (requestLock.current || busy || selectionBusy) return;
    requestLock.current = true;
    setBusy(true);
    try {
      const detail = await api<StyleReferenceDetail>(`/api/projects/${projectId}/style-references/${reference.id}`);
      if (!alive.current) return;
      formSnapshot.current = [detail.title, detail.content];
      setEditingId(reference.id);
      setTitle(detail.title);
      setContent(detail.content);
      setModalOpen(true);
    } catch (error) {
      notify(error instanceof Error ? error.message : "参考文本加载失败", "error");
    } finally {
      requestLock.current = false;
      if (alive.current) setBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (requestLock.current || !title.trim() || content.trim().length < 500) return;
    requestLock.current = true;
    setBusy(true);
    try {
      if (editingId) {
        const updated = await putJson<StyleReferenceDetail>(`/api/projects/${projectId}/style-references/${editingId}`, { title, content });
        setSelectedId(updated.id);
        notify(updated.status === "completed" ? "文风参考已更新" : "文风参考已更新，分析 Agent 正在重新生成画像", "success");
      } else {
        const created = await postJson<StyleReference>(`/api/projects/${projectId}/style-references`, { title, content });
        setSelectedId(created.id);
        notify("参考文本已加入全站文风库，分析完成后可在各小说中启用", "success");
      }
      setModalOpen(false);
      setEditingId("");
      setTitle("");
      setContent("");
      await load().catch((error) => { if (alive.current) setLoadError(`操作已提交，但列表刷新失败：${error instanceof Error ? error.message : "请重新加载"}`); });
    } catch (error) {
      notify(error instanceof Error ? error.message : editingId ? "更新失败" : "提交失败", "error");
    } finally {
      requestLock.current = false;
      if (alive.current) setBusy(false);
    }
  }

  async function chooseFile(file?: File) {
    if (!file) return;
    if (!/\.(txt|md)$/i.test(file.name)) {
      notify("目前支持 TXT 和 Markdown 文件", "error");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      notify("文件不能超过25MB", "error");
      return;
    }
    if (requestLock.current || busy || !await confirmDiscard(Boolean(content.trim()), "输入框中现有的参考文本（导入文件会替换它）")) return;
    requestLock.current = true; setBusy(true);
    try {
      const text = await file.text();
      if (!alive.current) return;
      setContent(text);
      if (!title) setTitle(file.name.replace(/\.(txt|md)$/i, ""));
    } catch (error) { notify(error instanceof Error ? error.message : "文件读取失败，原有输入仍保留", "error"); }
    finally { requestLock.current = false; if (alive.current) setBusy(false); }
  }

  async function toggle(reference: StyleReference) {
    if (busy || requestLock.current || selectionLock.current || !authorVoice) return;
    const active = authorVoice.active || !reference.active;
    selectionLock.current = true; setSelectionBusy(true);
    try {
      await patchJson(`/api/projects/${projectId}/style-references/${reference.id}`, { active });
      if (!alive.current) return;
      await load().catch((error) => { if (alive.current) setLoadError(`操作已提交，但列表刷新失败：${error instanceof Error ? error.message : "请重新加载"}`); });
      notify(active ? `已选用《${reference.title}》，本书声音已关闭${appliesAfterChapter ? `；第${appliesAfterChapter}章保持原文风，下一章生效` : ""}`
        : "已停用这份文风参考，其他选择保持不变", "success");
    } catch (cause) { if (alive.current) notify(cause instanceof Error ? cause.message : "状态更新失败", "error"); }
    finally { selectionLock.current = false; if (alive.current) setSelectionBusy(false); }
  }

  async function retry(reference: StyleReference) {
    if (requestLock.current || selectionBusy || reference.status === "analyzing") return;
    requestLock.current = true; setBusy(true);
    try {
      await postJson(`/api/projects/${projectId}/style-references/${reference.id}/retry`);
      notify("已重新启动文风分析", "info");
      await load().catch((error) => { if (alive.current) setLoadError(`操作已提交，但列表刷新失败：${error instanceof Error ? error.message : "请重新加载"}`); });
    } catch (error) {
      notify(error instanceof Error ? error.message : "重新分析失败", "error");
    } finally { requestLock.current = false; if (alive.current) setBusy(false); }
  }

  async function retryBlend() {
    if (requestLock.current || selectionBusy || blend.status === "analyzing") return;
    requestLock.current = true; setBusy(true);
    try {
      await postJson(`/api/projects/${projectId}/style-profile/retry`);
      notify("已重新启动总文风分析", "info");
      await load().catch((error) => { if (alive.current) setLoadError(`操作已提交，但列表刷新失败：${error instanceof Error ? error.message : "请重新加载"}`); });
    } catch (error) {
      notify(error instanceof Error ? error.message : "总文风分析重试失败", "error");
    } finally { requestLock.current = false; if (alive.current) setBusy(false); }
  }

  async function remove() {
    if (!deleteTarget || requestLock.current || selectionBusy) return;
    requestLock.current = true;
    setBusy(true);
    try {
      await api(`/api/style-references/${deleteTarget.id}`, { method: "DELETE" });
      setDeleteTarget(null);
      setSelectedId("");
      await load().catch((error) => { if (alive.current) setLoadError(`操作已提交，但列表刷新失败：${error instanceof Error ? error.message : "请重新加载"}`); });
      notify("文风参考已从全站库删除", "success");
    } catch (error) {
      notify(error instanceof Error ? error.message : "删除失败", "error");
    } finally {
      requestLock.current = false;
      if (alive.current) setBusy(false);
    }
  }

  return <div className="style-library">
    <AuthorVoicePanel projectId={projectId} notify={notify} refreshVersion={authorVoice?.version} selectionBusy={selectionBusy} onChange={() => void load().catch((cause) => notify(cause instanceof Error ? cause.message : "文风来源刷新失败", "error"))} />
    <p className="style-source-status" role="status">{authorVoice ? authorVoice.active ? `当前选择：本书声音「${authorVoice.title}」；下方文风未参与写作` : activeCount ? `当前选择：文风库 · ${activeCount} 份参考` : "本书声音已关闭；请选择下方文风参考" : "正在读取文风来源…"}{appliesAfterChapter !== null && <span>第{appliesAfterChapter}章保持原文风，新选择从下一章或新任务生效。</span>}</p>
    <header className="style-library-header">
      <div><span className="section-kicker">GLOBAL STYLE LIBRARY</span><h2>全站文风库</h2><p>{references.length} 份共享参考 · 当前小说已选 {activeCount} 份{authorVoice?.active ? " · 暂未使用" : ` · ${activeCount ? blendStatus(blend.status) : "未选参考"}`}</p></div>
      <button className="button button-primary" type="button" disabled={busy || selectionBusy} onClick={openCreate}><Plus size={15} />添加参考</button>
    </header>
    {loadError && <div className="ux-error" role="alert"><p>{loadError}</p><button className="button button-secondary" type="button" disabled={loading} onClick={() => void retryLoad()}>重新加载文风库</button></div>}
    {loading && <p className="ux-loading" role="status">正在读取文风库…</p>}
    {activeCount > 0 && <section className={`style-blend-panel blend-${blend.status}`}>
      <header>
        <span className="style-blend-icon">{blend.status === "analyzing" ? <LoaderCircle className="spin-icon" size={18} /> : <Sparkles size={18} />}</span>
        <div><strong>{authorVoice?.active ? "已选文风 · 暂未使用" : "文风库参考"}</strong><small>{activeCount === 1 ? "自动分析建议 + 原文片段" : `${activeCount} 份画像统一分析 + 均衡原文锚点`}</small></div>
        <span><StatusDot status={blend.status === "completed" ? "completed" : blend.status === "failed" ? "failed" : blend.status === "analyzing" ? "running" : "warning"} />{blendStatus(blend.status)}</span>
      </header>
      <div className="style-blend-sources">{blend.sourceTitles.map((source, index) => <span key={`${source}-${index}`}>{source}</span>)}{blend.anchorCount > 0 && <span>{blend.anchorCount} 段原文锚点 · {formatNumber(blend.anchorCharacters)} 字符</span>}</div>
      {blend.status === "analyzing" ? <p>正在分析特征并提炼写作锚点。</p>
        : blend.status === "failed" ? <div className="style-blend-failed"><p>{blend.error || "总文风分析失败"}</p><button className="button button-secondary" type="button" disabled={busy || selectionBusy} onClick={() => void retryBlend()}><RefreshCw size={14} />重新分析</button></div>
        : blend.profile ? <details className="style-blend-profile" open>
          <summary>{blend.profile.summary}</summary>
          <div className="style-blend-fields"><span><b>视角</b>{blend.profile.narrativeView}</span><span><b>句式</b>{blend.profile.sentenceRhythm}</span><span><b>对白</b>{blend.profile.dialogue}</span><span><b>节奏</b>{blend.profile.pacing}</span></div>
          <div className="style-blend-rules">{blend.profile.reusableRules.map((rule) => <span key={rule}><Check size={12} />{rule}</span>)}</div>
        </details> : null}
    </section>}
    {references.length ? <div className="style-library-grid">
      <div className="style-reference-list">{references.map((item) => <article key={item.id} className={selectedId === item.id ? "selected" : ""}>
        <button type="button" tabIndex={0} className="style-reference-select" aria-label={`查看文风参考 ${item.title}`} aria-pressed={selectedId === item.id} onClick={() => setSelectedId(item.id)}>
          <span className="style-reference-icon"><BookText size={17} /></span>
          <span className="style-reference-meta"><strong>{item.title}</strong><small>{formatNumber(item.characterCount)} 字符 · 采样 {formatNumber(item.sampledCharacters)}</small><span><StatusDot status={item.status === "completed" ? "completed" : item.status === "failed" ? "failed" : item.status === "analyzing" ? "running" : "warning"} />{styleStatus(item.status)}</span></span>
        </button>
        <button type="button" tabIndex={0} role="switch" aria-label={`${item.active && !authorVoice?.active ? "停用" : "启用"}${item.title}`} aria-checked={item.active && !authorVoice?.active} className={`toggle ${item.active && !authorVoice?.active ? "on" : ""}`} disabled={busy || selectionBusy || !authorVoice || item.status !== "completed"} onClick={(event) => { event.stopPropagation(); void toggle(item); }}><span /></button>
      </article>)}</div>
      <section className="style-profile-view">{selected ? <>
        <header><div><strong>{selected.title}</strong><small>{selected.active ? "已选入文风库" : "全站可用 · 当前小说未启用"}</small></div><div>
          <button className="icon-button" type="button" title="编辑参考" disabled={busy || selectionBusy || selected.status === "analyzing"} onClick={() => void openEdit(selected)}><FilePenLine size={15} /></button>
          {selected.status === "failed" && <button className="icon-button" type="button" title="重新分析" disabled={busy || selectionBusy} onClick={() => void retry(selected)}><RefreshCw size={15} /></button>}
          <button className="icon-button danger" type="button" title="删除全站参考" disabled={busy || selectionBusy || selected.status === "analyzing"} onClick={() => setDeleteTarget(selected)}><Trash2 size={15} /></button>
        </div></header>
        {selected.status === "analyzing" ? <div className="style-analyzing"><LoaderCircle className="spin-icon" size={24} /><strong>文风分析中</strong><span>正在抽样并生成文风画像</span></div> : selected.status === "failed" ? <div className="style-failed"><strong>分析失败</strong><span>{selected.error}</span></div> : selected.profile ? <div className="style-profile-content">
          <p className="style-summary">{selected.profile.summary}</p>
          <div className="style-profile-fields">{[["视角", selected.profile.narrativeView], ["叙述距离", selected.profile.narrativeDistance], ["句式节奏", selected.profile.sentenceRhythm], ["段落节奏", selected.profile.paragraphRhythm], ["词汇语言", selected.profile.diction], ["对白", selected.profile.dialogue], ["情绪", selected.profile.emotion], ["场景", selected.profile.sceneMethod], ["速度", selected.profile.pacing], ["钩子", selected.profile.hooks]].map(([label, value]) => <div key={label}><span>{label}</span><p>{value}</p></div>)}</div>
          <h3>可参考的声音观察</h3>{selected.profile.reusableRules.map((rule) => <div className="style-rule" key={rule}><Check size={12} />{rule}</div>)}
          <h3>禁止复制</h3>{selected.profile.prohibitedCopying.map((rule) => <div className="style-risk" key={rule}>{rule}</div>)}
        </div> : <EmptyState icon={<FileText size={22} />} title="等待分析" />}
      </> : <EmptyState icon={<Sparkles size={22} />} title="选择一份文风参考" detail="查看分析画像和当前小说的调用状态" />}</section>
    </div> : !loading && !loadError ? <EmptyState icon={<BookText size={24} />} title="全站文风库为空" detail="上传 TXT / MD，或直接粘贴参考文本" action={<button className="button button-secondary" type="button" disabled={busy} onClick={openCreate}>添加第一份参考</button>} /> : null}

    <Modal open={modalOpen} busy={busy} title={editingId ? "编辑文风参考" : "添加文风参考"} description={editingId ? "修改后将重新生成文风画像" : "全站共享参考，各小说独立选择调用"} width="large" onClose={() => void closeEditor()} footer={<><button className="button button-ghost" type="button" onClick={() => void closeEditor()} disabled={busy}>取消</button><button className="button button-primary" type="submit" form="style-reference-form" disabled={busy || title.trim().length < 1 || content.trim().length < 500}>{busy ? <span className="spinner" /> : editingId ? <FilePenLine size={15} /> : <Sparkles size={15} />}{editingId ? "保存修改" : "开始分析"}</button></>}>
      <form id="style-reference-form" className="style-reference-form" onSubmit={submit}>
        <label><span>参考名称</span><input data-autofocus disabled={busy} required value={title} onChange={(event) => setTitle(event.target.value)} placeholder="例如：快节奏男频参考A" /></label>
        <label className="style-file-picker"><Upload size={17} /><span><strong>选择 TXT / MD 文件</strong><small>最多25MB，也可以直接在下方编辑</small></span><input disabled={busy} type="file" accept=".txt,.md,text/plain,text/markdown" onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; void chooseFile(file); }} /></label>
        <label><span>参考文本 <small>{formatNumber(content.length)} 字符</small></span><textarea disabled={busy} value={content} onChange={(event) => setContent(event.target.value)} placeholder="粘贴小说正文或具有代表性的多个章节，至少500字符" /></label>
        <p className="ux-live-status" role="status">{!title.trim() ? "请填写参考名称。" : ""}{content.trim().length < 500 ? `参考文本至少 500 字符，还需 ${500 - content.trim().length} 字符。` : "文本长度已满足分析要求。"}{formDirty ? "有未保存修改。" : ""}</p>
        <div className="style-compliance-note">原文风格锚点仅校准节奏与语气，严禁复用专名与情节。</div>
      </form>
    </Modal>

    <Modal open={Boolean(deleteTarget)} busy={busy} title="删除全站文风参考" description="这会同时从所有小说的可选文风库中移除" width="small" onClose={() => !busy && setDeleteTarget(null)} footer={<><button className="button button-ghost" type="button" disabled={busy} onClick={() => setDeleteTarget(null)}>取消</button><button className="button button-danger" type="button" disabled={busy} onClick={() => void remove()}>{busy ? <span className="spinner dark" /> : <Trash2 size={15} />}确认删除</button></>}>
      <div className="style-delete-confirm"><Trash2 size={20} /><p>“{deleteTarget?.title}”的参考文本和分析画像将永久删除。各小说对它的启用选择也会一并清除。</p></div>
    </Modal>
  </div>;
}

function styleStatus(status: StyleReference["status"]): string {
  return ({ stored: "等待分析", analyzing: "分析中", completed: "分析完成", failed: "分析失败" })[status];
}

function blendStatus(status: ProjectStyleBlend["status"]): string {
  return ({ idle: "未启用总文风", analyzing: "总文风分析中", completed: "总文风已就绪", failed: "总文风分析失败" })[status];
}
